/**
 * useActingDealerManager — L251 S2/S6 (Round 8, 2026-09-05).
 *
 * WHICH manager is this session acting for on the dealer screens?
 *
 * A coordinator has no dealers of their own. `dealer-access.ts:82-95` gives
 * them the manager tier only by resolving `onBehalfOfUserId` through
 * `resolveDealerActingPerson`, and 403s when the field is absent. So the
 * screens need one id, resolved once, before they may call anything.
 *
 * 🔒 S6 — a coordinator books for EXACTLY ONE manager (`core/auth/on-behalf.ts`
 * refuses a named manager who is not their own `reportsTo`). This hook
 * therefore resolves and reports that manager; it is deliberately NOT a picker.
 *
 * 🔒 Why the /auth/me fallback exists: `POST /auth/login` does not return
 * `reportsTo` (backend `core/auth/service.ts` login include is
 * `{ role, department }`), while `GET /auth/me` does — and `stores/auth.ts`
 * fires its one `fetchMe()` at module load, BEFORE anyone signs in. Without the
 * fallback, a coordinator who has just signed in would have no manager in the
 * store and would meet the no-access screen for the whole session.
 *
 * 🔒 `unresolved` must render the page's existing no-access state. Calling with
 * a missing field is not an option: every row would 403, which is a worse
 * answer than a screen that says plainly it cannot open.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { dealerActsOnBehalf } from '@/lib/roles';

export interface ActingManagerRef {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

export interface ActingDealerManager {
  /** Sent on EVERY dealer request from this session. Null ⇒ acts as itself. */
  onBehalfOfUserId: string | null;
  /** The banner name. Null ⇒ nothing to announce. */
  name: string | null;
  /** Still asking. The screen must wait, not refuse. */
  resolving: boolean;
  /** Must act on behalf, but no manager could be found ⇒ render no-access. */
  unresolved: boolean;
}

const NOT_ACTING: ActingDealerManager = {
  onBehalfOfUserId: null, name: null, resolving: false, unresolved: false,
};

/** Mirrors `displayName` in backend `core/auth/on-behalf.ts`. */
export function actingManagerName(m: ActingManagerRef): string {
  const n = [m.firstName, m.lastName].filter(Boolean).join(' ').trim();
  return n || (m.email ?? '').trim() || m.id;
}

function refFrom(raw: unknown): ActingManagerRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  const id = typeof m.id === 'string' ? m.id.trim() : '';
  if (!id) return null;
  return {
    id,
    firstName: typeof m.firstName === 'string' ? m.firstName : null,
    lastName: typeof m.lastName === 'string' ? m.lastName : null,
    email: typeof m.email === 'string' ? m.email : null,
  };
}

export function useActingDealerManager(): ActingDealerManager {
  const { user } = useAuthStore();
  const callerId = (user as { id?: string } | null)?.id ?? '';
  const acts = dealerActsOnBehalf(user?.role?.name);
  const fromSession = refFrom((user as { reportsTo?: unknown } | null)?.reportsTo);

  const [fetched, setFetched] = useState<{ callerId: string; manager: ActingManagerRef | null } | null>(null);
  // One /auth/me per caller, ever — a re-render must not re-ask.
  const askedFor = useRef('');

  const needsFetch = acts && !!callerId && !fromSession;

  useEffect(() => {
    if (!needsFetch || askedFor.current === callerId) return;
    askedFor.current = callerId;
    let alive = true;
    api.get('/auth/me')
      .then((res) => {
        if (!alive) return;
        setFetched({ callerId, manager: refFrom(res.data?.data?.reportsTo ?? res.data?.reportsTo) });
      })
      .catch(() => {
        // Fail to the no-access state, never to a request with a blank field.
        if (alive) setFetched({ callerId, manager: null });
      });
    return () => { alive = false; };
  }, [needsFetch, callerId]);

  if (!acts) return NOT_ACTING;

  const manager = fromSession ?? (fetched?.callerId === callerId ? fetched.manager : null);
  if (manager) {
    return { onBehalfOfUserId: manager.id, name: actingManagerName(manager), resolving: false, unresolved: false };
  }
  const answered = !callerId || fetched?.callerId === callerId;
  return { onBehalfOfUserId: null, name: null, resolving: !answered, unresolved: answered };
}
